import { COUNTRIES } from "./CONSTANTS";

// grams to lbs
export const gramsToLbs = (grams) => {
  const lbs = grams / 453.592; // 1 lb = 453.592 grams
  return lbs.toFixed(2); // Round to 2 decimal places
};

export const ozToLbs = (oz) => {
  return (oz / 16).toFixed(2); // 1 lb = 16 oz
};

// generate random unique id
export const guidGenerator = () => {
  var S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return (
    S4() +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    "-" +
    S4() +
    S4() +
    S4()
  );
};

export const calculateCountdown = (dogProfile) => {
  const now = new Date();
  const euthanasiaDate = new Date(dogProfile?.euthanasiaDate + "T23:59:59");

  const difference = euthanasiaDate.getTime() - now.getTime();

  if (difference > 0) {
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / 1000 / 60) % 60);
    const seconds = Math.floor((difference / 1000) % 60);

    let countdownString = "";
    if (days > 0) {
      countdownString += `${days}d `;
    }
    if (hours > 0 || days > 0) {
      countdownString += `${hours}h `;
    }
    if (minutes > 0 || hours > 0 || days > 0) {
      countdownString += `${minutes}m `;
    }
    countdownString += `${seconds}s`;

    return countdownString.trim();
  } else {
    return "Time expired";
  }
};

export const unprocessedPledges = (pledges) => {
  const uPledges = Object.values(pledges || {});
  return uPledges.filter((pledge) => pledge.status === "requires_captures");
};

export const getScoreColor = (score) => {
  if (score < 1 || score > 7) return "#808080"; // Gray for invalid scores
  if (score >= 1 && score < 2) return "#8B0000"; // Dark red for type 1
  if (score >= 2 && score < 3) return "#32CD32"; // Lime green for type 2 (ideal)
  if (score >= 3 && score < 4) return "#9ACD32"; // Yellow green for type 3
  if (score >= 4 && score < 5) return "#FFA500"; // Orange for type 4
  if (score >= 5 && score < 6) return "#FF6347"; // Tomato for type 5
  if (score >= 6 && score < 7) return "#FF4500"; // Orange red for type 6
  return "#8B0000"; // Dark red for type 7
};

export const getConsistencyCharacteristics = (level) => {
  if (level <= 25) {
    return [
      { emoji: "🪨", text: "Hard and dry" },
      { emoji: "🧱", text: "Difficult to pass" },
      { emoji: "🥜", text: "May resemble small pellets or pebbles" },
    ];
  } else if (level <= 50) {
    return [
      { emoji: "🍌", text: "Firm but soft" },
      { emoji: "✅", text: "Well-formed and easy to pass" },
      { emoji: "👍", text: "Ideal consistency" },
    ];
  } else if (level <= 75) {
    return [
      { emoji: "🍦", text: "Soft and moist" },
      { emoji: "💧", text: "Losing definition" },
      { emoji: "🧽", text: "May leave residue" },
    ];
  } else {
    return [
      { emoji: "💦", text: "Very loose and watery" },
      { emoji: "🥣", text: "No solid pieces" },
      { emoji: "🍯", text: "Resembles pudding or gravy" },
    ];
  }
};

// find state from location props
export const findState = (locProps) => {
  let USA = COUNTRIES.UNITED_STATES;
  const arrUSA = Object.keys(USA);
  let stateVar = null;
  locProps.map((locObj) => {
    for (let i = 0; i < arrUSA.length; i++) {
      if (locObj.long_name === arrUSA[i]) {
        stateVar = arrUSA[i];
      }
    }
  });
  return stateVar;
};

export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result.toDateString();
};

// reorganizes / cleans the data set
export const enhanceContextData = (user) => {
  const userPreferences = user?.contextData
    ? Object.values(user?.contextData).map((elem) => {
        return elem[0];
      })
    : [];
  return userPreferences;
};

export const calculateSavings = (pricing) => {
  const weeklyPrice = pricing.find((plan) => plan.title === "Weekly").price;
  const yearlyPrice = pricing.find((plan) => plan.title === "Yearly").price;
  const weeksInYear = 52;

  const savingsPercentage =
    ((weeklyPrice * weeksInYear - yearlyPrice) / (weeklyPrice * weeksInYear)) *
    100;
  return Math.round(savingsPercentage);
};

export const calculateDogTimeRemaining = (dog, shelterClosingTime) => {
  if (!dog.euthanasiaDate) {
    return {
      timeRemainingMs: Infinity,
      timeRemainingFormatted: 'N/A'
    };
  }

  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const isToday = dog.euthanasiaDate === today;

  // Determine deadline time
  let deadline = "T23:59:59";
  if (isToday && shelterClosingTime) {
    const closeTime = shelterClosingTime.toString().padStart(4, "0");
    const hours = closeTime.slice(0, 2);
    const minutes = closeTime.slice(2, 4);
    deadline = `T${hours}:${minutes}:00`;
  }

  const euthanasiaDateTime = new Date(dog.euthanasiaDate + deadline);
  const difference = euthanasiaDateTime.getTime() - now.getTime();

  let timeRemainingFormatted;
  if (difference <= 0) {
    timeRemainingFormatted = "EXPIRED";
  } else {
    const totalDays = Math.floor(difference / (1000 * 60 * 60 * 24));
    const totalHours = Math.floor(difference / (1000 * 60 * 60));
    const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((difference / (1000 * 60)) % 60);

    if (totalDays > 0) {
      timeRemainingFormatted = `${totalDays} DAY${totalDays > 1 ? 'S' : ''}`;
    } else if (totalHours > 0) {
      timeRemainingFormatted = `${totalHours} HOUR${totalHours > 1 ? 'S' : ''}`;
    } else if (minutes > 0) {
      timeRemainingFormatted = `${minutes} MIN${minutes > 1 ? 'S' : ''}`;
    } else {
      timeRemainingFormatted = "< 1 MIN";
    }
  }

  return {
    timeRemainingMs: difference > 0 ? difference : -1,
    timeRemainingFormatted
  };
};

export const formatDogAge = (ageString) => {
  if (!ageString) return "N/A";

  const ageLower = ageString.toLowerCase();

  // Extract years
  const yearsMatch = ageLower.match(/(\d+)\s*year/);
  const years = yearsMatch ? parseInt(yearsMatch[1]) : 0;

  // Extract months
  const monthsMatch = ageLower.match(/(\d+)\s*month/);
  const months = monthsMatch ? parseInt(monthsMatch[1]) : 0;

  // If has years (with or without months), return years only
  if (years > 0) {
    return `${years}yo`;
  }

  // If only months
  if (months > 0) {
    return `${months}mo`;
  }

  return "N/A";
};

export const groupPledgesByAnimalId = (pledges) => {
  const pledgeMap = {};
  pledges.forEach(pledge => {
    const animalId = pledge.animalId;
    if (!pledgeMap[animalId]) {
      pledgeMap[animalId] = 0;
    }
    pledgeMap[animalId] += pledge.displayAmount || 0;
  });
  return pledgeMap; // Returns { "A123": 150, "A456": 200, ... }
};

// Format milliseconds into countdown timer format
// >= 24 hours: "X Day/s Y Hours"
// < 24 hours: "HH:MM:SS"
export const formatCountdownTimer = (differenceMs) => {
  if (differenceMs <= 0) {
    return "EXPIRED";
  }

  // If >= 24 hours, show "X Day/s Y Hours"
  if (differenceMs >= 86400000) { // 24 hours in ms
    const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((differenceMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `${days} Day${days !== 1 ? 's' : ''} ${hours} Hour${hours !== 1 ? 's' : ''}`;
  }

  // If < 24 hours, show HH:MM:SS
  const totalHours = Math.floor(differenceMs / (1000 * 60 * 60));
  const minutes = Math.floor((differenceMs / (1000 * 60)) % 60);
  const seconds = Math.floor((differenceMs / 1000) % 60);
  const pad = (n) => n.toString().padStart(2, "0");

  return `${pad(totalHours)}:${pad(minutes)}:${pad(seconds)}`;
};

// Sort dogs by urgency (time remaining + days at shelter as tiebreaker)
export const sortDogsByUrgency = (dogs, sortBy) => {
  const sorted = [...dogs];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case "most": // Most urgent (least time remaining)
        if (a.timeRemainingMs === -1) return 1;
        if (b.timeRemainingMs === -1) return -1;

        const timeDiffMost = a.timeRemainingMs - b.timeRemainingMs;
        if (timeDiffMost === 0) {
          // Secondary sort: more days at shelter = more urgent
          return (b.daysAtShelter || 0) - (a.daysAtShelter || 0);
        }
        return timeDiffMost;

      case "least": // Least urgent (most time remaining)
        if (a.timeRemainingMs === -1) return -1;
        if (b.timeRemainingMs === -1) return 1;

        const timeDiffLeast = b.timeRemainingMs - a.timeRemainingMs;
        if (timeDiffLeast === 0) {
          // Secondary sort: fewer days at shelter = less urgent
          return (a.daysAtShelter || 0) - (b.daysAtShelter || 0);
        }
        return timeDiffLeast;

      default:
        return 0;
    }
  });

  return sorted;
};
